import styled from 'styled-components';
import {
  Badge,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { ReactComponent as SortIcon } from '../../../../../assets/icons/sort-icon.svg';
import colors from '../../../../../styles/colors';
import { ToggleButton as BaseToggleButton } from '../../../../layout/components/ToggleButton';

const DRAWER_WIDTH_EXPANDED = 280;
const DRAWER_WIDTH_COLLAPSED = 80;

const getDrawerStyle = (isCollapsed) => ({
  display: { xs: 'none', sm: 'block' },
  flex: 1,
  minHeight: 0,
  '& .MuiDrawer-paper': {
    boxSizing: 'border-box',
    position: 'unset',
    width: isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED,
    height: '100%',
    maxHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width 0.2s ease-in-out',
    // Separates the filter column from the tinted canvas beside it.
    borderRight: `1px solid ${colors.shades.gamma1}`,
    overflow: 'hidden',
    overflowX: 'hidden',
    '& .MuiList-root': {
      overflowX: 'hidden',
    },
  },
});

const StyledBadge = styled(Badge)`
  margin-right: 10px;
  .MuiBadge-badge {
    background-color: ${colors.shades.primary3};
    border: 1px solid ${colors.shades.primary4};
  }
`;

const CollapsedBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: ${colors.base.secondary2};
  background-color: ${colors.shades.primary3};
  border-radius: 10px;
  padding: 2px 0px;
  min-width: 28px;
  text-align: center;
  margin-left: 3px;
  flex-shrink: 0;
`;

const FeedListFooter = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  margin-top: auto;
  min-height: 90px;
  padding-bottom: 15px;
`;

const StyledList = styled(List)`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
`;

const FeedListContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: ${({ $isCollapsed }) => ($isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED)}px;
  transition: width 0.2s ease-in-out;
  flex-shrink: 0;
`;

// A lighter tint of the header's mint: related enough to tie the filter column to the
// chrome above it, but deliberately not the same value — matching it exactly made the
// two read as one undifferentiated block.
const FilterContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: ${colors.shades.secondary5};
  padding: 15px 10px;
  gap: 5px;
`;

const FilterBar = styled.div`
  display: flex;
  min-height: 0;
  flex-shrink: 0;
`;

const SortContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 2px;
  cursor: pointer;
`;

const StyledSortIcon = styled(SortIcon)`
  width: 18px;
  height: 18px;
  margin-left: 5px;

  path {
    stroke: ${colors.shades.gamma2};
  }
`;

const ToggleButton = styled(BaseToggleButton)`
  && {
    margin-left: ${({ $isCollapsed }) => ($isCollapsed ? 'auto' : '0')};
    margin-right: ${({ $isCollapsed }) => ($isCollapsed ? 'auto' : '0')};
    margin-top: ${({ $isCollapsed }) => ($isCollapsed ? '8px' : '0')};
  }
`;

const StyledListItem = styled(ListItem)`
  && {
    padding: ${({ $isCollapsed }) => ($isCollapsed ? '12px 8px' : '8px 16px')};
    justify-content: ${({ $isCollapsed }) => ($isCollapsed ? 'center' : 'flex-start')};
    gap: ${({ $isCollapsed }) => ($isCollapsed ? '4px' : '0')};
    overflow: hidden;
    min-width: 0;

    .MuiListItemIcon-root {
      min-width: ${({ $isCollapsed }) => ($isCollapsed ? 'auto' : '56px')};
      margin-right: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '8px')};
      flex-shrink: 0;
    }
  }
`;

const StyledListItemText = styled(ListItemText)`
  && {
    white-space: nowrap;
    overflow: hidden;
    transition: max-width 0.2s ease, opacity 0.12s ease;
    transition-delay: ${({ $isCollapsed }) => ($isCollapsed ? '0s' : '0.12s')};
    max-width: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '100%')};
    opacity: ${({ $isCollapsed }) => ($isCollapsed ? 0 : 1)};
    flex: ${({ $isCollapsed }) => ($isCollapsed ? '0 0 auto' : '1 1 auto')};
  }
`;

export {
  getDrawerStyle,
  StyledBadge,
  CollapsedBadge,
  FeedListFooter,
  StyledList,
  FeedListContainer,
  FilterContainer,
  FilterBar,
  SortContainer,
  StyledSortIcon,
  ToggleButton,
  StyledListItem,
  StyledListItemText,
};
